import LoadingImage from "@/components/LoadingImage";
import { getCoverImage } from "@/lib/trip-helpers";

interface DestinationHeroProps {
  title: string;
  destination?: string;
  coverImage?: string;
  dateRange?: string | null;
  dayCount?: number;
  stopCount?: number;
}

// Editorial hero banner for the top of the itinerary column. A real
// destination photo with the trip title + meta overlaid in serif, so the
// planner opens like a travel guide cover rather than a text list.
const DestinationHero = ({
  title,
  destination,
  coverImage,
  dateRange,
  dayCount,
  stopCount,
}: DestinationHeroProps) => {
  const src = coverImage?.trim() || getCoverImage(destination || title || "");

  const meta = [
    dateRange,
    dayCount ? `${dayCount} ${dayCount === 1 ? "day" : "days"}` : null,
    stopCount ? `${stopCount} ${stopCount === 1 ? "stop" : "stops"}` : null,
  ].filter(Boolean);

  return (
    <div className="relative h-44 sm:h-52 overflow-hidden border border-rule">
      <LoadingImage
        src={src}
        alt={destination || title}
        containerClassName="absolute inset-0"
        className="h-full w-full object-cover"
      />
      {/* Dark gradient for legible overlay text. */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5">
        {destination && (
          <div className="kicker text-[#fff]/80 mb-1.5">
            {destination}
          </div>
        )}
        <h1 className="font-serif text-2xl sm:text-3xl text-[#fff] leading-tight drop-shadow-sm">
          {title || "Your trip"}
        </h1>
        {meta.length > 0 && (
          <div className="mt-1.5 text-xs text-[#fff]/80 flex items-center gap-2 flex-wrap">
            {meta.map((m, i) => (
              <span key={i} className="flex items-center gap-2">
                {i > 0 && <span className="text-[#fff]/40">·</span>}
                {m}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DestinationHero;
